import { useEffect, useState } from 'react';
import { API_SCOPE_GROUPS, SCOPE_READ_COMPANION, scopeLabel, type ApiScope } from '../lib/apiScopes';
import { useAuth, useFeature } from '../context/AuthContext';
import { apiGet, apiPost } from '../lib/api';
import { applyFontSize, currentFontSize, FONT_SIZE_OPTIONS, type FontSize } from '../lib/fontSize';
import { parseMessageOrder, parseRowColor, savePreferences, type MessageOrder, type RowColor } from '../lib/preferences';
import { applyThemePref, parseThemePref, storedTheme, type ThemePref } from '../lib/theme';
import { authFetch } from '../lib/auth';

interface MfaStatus {
  enabled: boolean;
  configured: boolean;
}

interface ApiKey {
  id_api_key: number;
  label: string;
  key_prefix: string;
  scopes: string[];
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  revoked_at: string | null;
}

export function ProfilePage() {
  return (
    <div>
      <h1 style={{ fontSize: 'var(--fs-2xl)', fontWeight: 600, marginTop: 0 }}>Mon profil</h1>
      <div className="grid-2" style={{ gap: 16 }}>
        <DisplayCard />
        <PasswordCard />
        <MfaCard />
        <ApiKeysCard />
      </div>
    </div>
  );
}

// ---- Affichage : taille du texte, ordre du fil, thème ---------------------
// Enregistrés pour le compte (base), donc retrouvés sur tout appareil.
type PrefKey = 'font_size' | 'message_order' | 'theme' | 'row_color';

const MESSAGE_ORDER_OPTIONS: readonly { value: MessageOrder; label: string }[] = [
  { value: 'recent_first', label: 'Plus récents en haut' },
  { value: 'oldest_first', label: 'Plus anciens en haut' },
];
const ROW_COLOR_OPTIONS: readonly { value: RowColor; label: string }[] = [
  { value: 'etat', label: 'Case état seulement' },
  { value: 'ligne', label: 'Toute la ligne' },
];
const THEME_OPTIONS: readonly { value: ThemePref; label: string }[] = [
  { value: 'light', label: 'Clair' },
  { value: 'dark', label: 'Sombre' },
  { value: 'system', label: "Selon l'appareil" },
];

function DisplayCard() {
  const { user, refresh } = useAuth();
  const [size, setSize] = useState<FontSize>(currentFontSize);
  const order = parseMessageOrder(user?.preferences?.message_order);
  const [orderSel, setOrderSel] = useState<MessageOrder>(order);
  const [theme, setTheme] = useState<ThemePref>(() => parseThemePref(user?.preferences?.theme) ?? (storedTheme() ?? 'system'));
  const [rowColor, setRowColor] = useState<RowColor>(() => parseRowColor(user?.preferences?.row_color));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok?: string; err?: string } | null>(null);

  // Applique tout de suite (aperçu), enregistre ensuite ; échec : on revient.
  const save = async (key: PrefKey, value: string, apply: () => void, revert: () => void) => {
    setMsg(null);
    setBusy(true);
    apply();
    try {
      await savePreferences({ [key]: value });
      await refresh();
      setMsg({ ok: 'Préférence enregistrée pour votre compte' });
    } catch (e) {
      revert();
      setMsg({ err: e instanceof Error ? e.message : 'Préférence non enregistrée' });
    } finally {
      setBusy(false);
    }
  };

  const chooseSize = (next: FontSize) => {
    const prev = size;
    void save(
      'font_size',
      next,
      () => { setSize(next); applyFontSize(next); },
      () => { setSize(prev); applyFontSize(prev); },
    );
  };
  const chooseOrder = (next: MessageOrder) => {
    const prev = orderSel;
    void save('message_order', next, () => setOrderSel(next), () => setOrderSel(prev));
  };
  const chooseRowColor = (next: RowColor) => {
    const prev = rowColor;
    void save('row_color', next, () => setRowColor(next), () => setRowColor(prev));
  };
  const chooseTheme = (next: ThemePref) => {
    const prev = theme;
    void save(
      'theme',
      next,
      () => { setTheme(next); applyThemePref(next); },
      () => { setTheme(prev); applyThemePref(prev); },
    );
  };

  return (
    <Card title="Affichage">
      <Choice id="pref-taille" label="Taille du texte" options={FONT_SIZE_OPTIONS} value={size} disabled={busy} onChoose={chooseSize} />
      <Choice id="pref-ordre" label="Ordre des messages" options={MESSAGE_ORDER_OPTIONS} value={orderSel} disabled={busy} onChoose={chooseOrder} />
      <Choice id="pref-couleur" label="Couleur des points" options={ROW_COLOR_OPTIONS} value={rowColor} disabled={busy} onChoose={chooseRowColor} />
      <Choice id="pref-theme" label="Thème" options={THEME_OPTIONS} value={theme} disabled={busy} onChoose={chooseTheme} />
      {msg?.err && <FormMsg err>{msg.err}</FormMsg>}
      {msg?.ok && <FormMsg>{msg.ok}</FormMsg>}
    </Card>
  );
}

function Choice<T extends string>({
  id,
  label,
  options,
  value,
  disabled,
  onChoose,
}: {
  id: string;
  label: string;
  options: readonly { value: T; label: string }[];
  value: T;
  disabled: boolean;
  onChoose: (v: T) => void;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div id={id} style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginBottom: 6 }}>
        {label}
      </div>
      <div role="radiogroup" aria-labelledby={id} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            className={value === o.value ? 'btn primary' : 'btn'}
            disabled={disabled}
            onClick={() => value !== o.value && onChoose(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---- Password -----------------------------------------------------------
function PasswordCard() {
  const passwordOpen = useFeature('password_change');
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok?: string; err?: string } | null>(null);

  const submit = async () => {
    setMsg(null);
    if (newPwd !== confirm) return setMsg({ err: 'Les mots de passe ne correspondent pas' });
    if (newPwd.length < 8) return setMsg({ err: 'Le nouveau mdp doit faire au moins 8 caractères' });
    if (!/^[A-Za-z0-9@.-]+$/.test(newPwd)) {
      return setMsg({ err: 'Lettres sans accent, chiffres et @ . - uniquement (compatibilité avec Jarvis V1)' });
    }
    setBusy(true);
    try {
      await apiPost('/api/me/password', { old_password: oldPwd, new_password: newPwd });
      setMsg({ ok: 'Mot de passe mis à jour' });
      setOldPwd('');
      setNewPwd('');
      setConfirm('');
    } catch (e) {
      setMsg({ err: e instanceof Error ? e.message : 'Erreur' });
    } finally {
      setBusy(false);
    }
  };

  if (!passwordOpen) {
    return (
      <Card title="Mot de passe">
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>
          Le changement de mot de passe se fait encore dans Jarvis V1 pendant la transition.
        </div>
      </Card>
    );
  }

  return (
    <Card title="Mot de passe">
      <Input type="password" label="Mot de passe actuel" value={oldPwd} onChange={setOldPwd} autoComplete="current-password" />
      <Input type="password" label="Nouveau (8 caractères min. : lettres sans accent, chiffres et @ . - (compatibilité avec Jarvis V1))" value={newPwd} onChange={setNewPwd} autoComplete="new-password" />
      <Input type="password" label="Confirmer" value={confirm} onChange={setConfirm} autoComplete="new-password" />
      {msg?.err && <FormMsg err>{msg.err}</FormMsg>}
      {msg?.ok && <FormMsg>{msg.ok}</FormMsg>}
      <button className="btn primary" onClick={submit} disabled={busy || !oldPwd || !newPwd || !confirm} style={{ marginTop: 8 }}>
        {busy ? 'Mise à jour…' : 'Mettre à jour'}
      </button>
      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginTop: 8 }}>
        ⚠ Format compatible Jarvis Perl (SHA1 double MySQL). Vous resterez connecté avec ce mdp sur l&apos;app Perl.
      </div>
    </Card>
  );
}

// ---- MFA ----------------------------------------------------------------
function MfaCard() {
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [setupData, setSetupData] = useState<{ qr_data_url: string; secret_b32: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [disablePwd, setDisablePwd] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => apiGet<MfaStatus>('/api/me/mfa/status').then(setStatus).catch(() => {});
  useEffect(() => { void reload(); }, []);

  const startSetup = async () => {
    setBusy(true);
    setErr(null);
    try {
      const d = await apiPost<{ qr_data_url: string; secret_b32: string }>('/api/me/mfa/setup', {});
      setSetupData(d);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setErr(null);
    try {
      const d = await apiPost<{ recovery_codes: string[] }>('/api/me/mfa/verify', { code });
      setRecoveryCodes(d.recovery_codes);
      setSetupData(null);
      setCode('');
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await authFetch('/api/me/mfa', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: disablePwd }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Erreur');
      setDisablePwd('');
      setRecoveryCodes(null);
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={`MFA / 2FA ${status?.enabled ? '✓ Actif' : '— inactif'}`}>
      {!status && <div style={{ color: 'var(--text-mute)' }}>Chargement…</div>}

      {status && !status.enabled && !setupData && !recoveryCodes && (
        <>
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)' }}>
            Activez l&apos;authentification à deux facteurs avec Google Authenticator, Authy ou 1Password.
          </p>
          <button className="btn primary" onClick={startSetup} disabled={busy}>
            {busy ? 'Préparation…' : 'Activer le MFA'}
          </button>
        </>
      )}

      {setupData && (
        <>
          <p style={{ fontSize: 'var(--fs-sm)' }}>Scannez ce QR code avec votre app TOTP :</p>
          <img src={setupData.qr_data_url} alt="QR code TOTP" style={{ display: 'block', margin: '8px 0' }} />
          <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>
            Ou saisissez ce secret manuellement : <code>{setupData.secret_b32}</code>
          </p>
          <Input label="Code à 6 chiffres" value={code} onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))} placeholder="123456" autoComplete="one-time-code" />
          {err && <FormMsg err>{err}</FormMsg>}
          <button className="btn primary" onClick={verify} disabled={busy || code.length !== 6} style={{ marginTop: 8 }}>
            {busy ? 'Vérification…' : 'Activer'}
          </button>
        </>
      )}

      {recoveryCodes && (
        <>
          <div style={{ background: 'var(--warning-soft)', border: '1px solid var(--warning-border)', borderRadius: 6, padding: 10, marginBottom: 10, fontSize: 'var(--fs-xs)' }}>
            ⚠ <strong>Imprimez ou copiez ces 10 codes de récupération maintenant.</strong> Ils ne seront plus jamais affichés.
            Chacun usage unique en cas de perte du device TOTP.
          </div>
          <pre style={{ background: 'var(--bg-soft)', padding: 10, borderRadius: 6, fontSize: 'var(--fs-xs)', fontFamily: 'ui-monospace, Menlo, monospace' }}>
            {recoveryCodes.join('\n')}
          </pre>
          <button className="btn" onClick={() => navigator.clipboard.writeText(recoveryCodes.join('\n'))}>
            Copier les codes
          </button>
          <button className="btn ghost" onClick={() => setRecoveryCodes(null)} style={{ marginLeft: 8 }}>
            Fermer
          </button>
        </>
      )}

      {status?.enabled && !recoveryCodes && (
        <>
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--success)' }}>✓ MFA actif sur votre compte</p>
          <Input type="password" label="Mot de passe pour désactiver" value={disablePwd} onChange={setDisablePwd} />
          {err && <FormMsg err>{err}</FormMsg>}
          <button className="btn" onClick={disable} disabled={busy || !disablePwd} style={{ marginTop: 8, color: 'var(--danger)', borderColor: 'var(--danger-border)' }}>
            Désactiver le MFA
          </button>
        </>
      )}
    </Card>
  );
}

// ---- API keys -----------------------------------------------------------
function ApiKeysCard() {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>(['tickets:read']);
  const [expDays, setExpDays] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{ api_key: string; label: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => apiGet<{ items: ApiKey[] }>('/api/me/api-keys').then((d) => setKeys(d.items)).catch(() => {});
  useEffect(() => { void reload(); }, []);

  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      const d = await apiPost<{ api_key: string; label: string }>('/api/me/api-keys', {
        label,
        scopes,
        expires_in_days: expDays ? Number(expDays) : undefined,
      });
      setCreated(d);
      setLabel('');
      setExpDays('');
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: number) => {
    if (!confirm('Révoquer cette clé ? Action irréversible.')) return;
    const res = await authFetch(`/api/me/api-keys/${id}`, { method: 'DELETE' });
    if (res.ok) await reload();
  };

  return (
    <Card title="Clés API" wide>
      <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>
        Pour authentifier des scripts ou apps externes via <code>Authorization: Bearer jxk_…</code>.
        Une clé n’agit qu’avec les droits cochés à sa création, et jamais au-delà des vôtres.
      </p>

      {created && (
        <div style={{ background: 'var(--success-soft)', border: '1px solid var(--success-border)', borderRadius: 6, padding: 10, marginBottom: 12 }}>
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--success)', marginBottom: 4 }}>
            ✓ Clé &quot;{created.label}&quot; créée — <strong>copiez la maintenant</strong>, elle ne sera plus jamais affichée
          </div>
          <code style={{ display: 'block', background: 'var(--bg)', padding: 8, borderRadius: 4, fontSize: 'var(--fs-xs)', wordBreak: 'break-all' }}>
            {created.api_key}
          </code>
          <button className="btn" onClick={() => navigator.clipboard.writeText(created.api_key)} style={{ marginTop: 6, fontSize: 'var(--fs-xs)' }}>
            Copier
          </button>
          <button className="btn ghost" onClick={() => setCreated(null)} style={{ marginLeft: 6, fontSize: 'var(--fs-xs)' }}>
            Fermer
          </button>
        </div>
      )}

      {keys && keys.filter((k) => !k.revoked_at).map((k) => (
        <div key={k.id_api_key} style={{ display: 'flex', padding: '8px 0', borderBottom: '1px dashed var(--border)', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500, fontSize: 'var(--fs-sm)' }}>{k.label}</div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', fontFamily: 'ui-monospace, Menlo, monospace' }}>
              {k.key_prefix}… · droits : {k.scopes.map(scopeLabel).join(', ')}
              {k.expires_at && ` · expire ${new Date(k.expires_at).toLocaleDateString('fr-FR')}`}
              {k.last_used_at && ` · vue ${new Date(k.last_used_at).toLocaleDateString('fr-FR')}`}
            </div>
          </div>
          <button className="btn ghost" onClick={() => revoke(k.id_api_key)} style={{ fontSize: 'var(--fs-xs)', color: 'var(--danger)' }}>
            Révoquer
          </button>
        </div>
      ))}
      {keys && keys.filter((k) => !k.revoked_at).length === 0 && (
        <div style={{ color: 'var(--text-mute)', fontSize: 'var(--fs-sm)', padding: '8px 0' }}>Aucune clé active.</div>
      )}

      <div style={{ marginTop: 12, padding: 12, background: 'var(--bg-soft)', borderRadius: 6 }}>
        <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, marginBottom: 8 }}>Nouvelle clé API</div>
        <Input label="Label" value={label} onChange={setLabel} placeholder="ex: monitoring-cron" />
        <ScopePicker value={scopes} onChange={setScopes} />
        <Input label="Expiration (jours, vide = jamais)" value={expDays} onChange={setExpDays} placeholder="365" />
        {err && <FormMsg err>{err}</FormMsg>}
        <button className="btn primary" onClick={create} disabled={busy || !label || scopes.length === 0} style={{ marginTop: 8 }}>
          {busy ? 'Création…' : '+ Créer la clé'}
        </button>
      </div>
    </Card>
  );
}

/**
 * Choix des droits d'une clé : catalogue fermé (api/src/lib/apiScopes.ts),
 * présenté en lecture / écriture. Écrire sans lire n'a pas de sens : cocher
 * une écriture coche la lecture du même domaine quand elle existe.
 */
function ScopePicker({ value, onChange }: { value: ApiScope[]; onChange: (v: ApiScope[]) => void }) {
  const toggle = (s: ApiScope) => {
    if (value.includes(s)) {
      onChange(value.filter((v) => v !== s));
      return;
    }
    const next = new Set<ApiScope>([...value, s]);
    const companion = SCOPE_READ_COMPANION[s];
    if (companion) next.add(companion);
    onChange(API_SCOPE_GROUPS.flatMap((g) => g.scopes.map((o) => o.scope)).filter((x) => next.has(x)));
  };
  return (
    <fieldset style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', margin: '8px 0' }}>
      <legend style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, padding: '0 4px' }}>Droits de la clé</legend>
      {API_SCOPE_GROUPS.map((g) => (
        <div key={g.title} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 16px', padding: '4px 0' }}>
          <span style={{ minWidth: 150, fontSize: 'var(--fs-sm)' }}>{g.title}</span>
          {g.scopes.map((o) => (
            <label key={o.scope} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-sm)' }}>
              <input type="checkbox" checked={value.includes(o.scope)} onChange={() => toggle(o.scope)} />
              {o.label}
            </label>
          ))}
        </div>
      ))}
      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginTop: 4 }}>
        Lecture : consulter. Écriture : créer, modifier, répondre, changer l’état ou les traitants. Les temps (décompte du crédit client) sont à part.
      </div>
    </fieldset>
  );
}

// ---- UI helpers ---------------------------------------------------------
function Card({ title, children, wide }: { title: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div
      className={wide ? 'card-wide' : undefined}
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 16,
        background: 'var(--bg)',
      }}
    >
      <h3 style={{ margin: '0 0 12px', fontSize: 'var(--fs-base)', fontWeight: 600 }}>{title}</h3>
      {children}
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div style={{ marginBottom: 8 }}>
      <label style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginBottom: 2 }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={{ width: '100%', padding: 8, border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 'var(--fs-sm)' }}
      />
    </div>
  );
}

function FormMsg({ children, err }: { children: React.ReactNode; err?: boolean }) {
  return (
    <div
      style={{
        marginTop: 8,
        padding: '6px 10px',
        background: err ? 'var(--danger-soft)' : 'var(--success-soft)',
        border: `1px solid ${err ? 'var(--danger-border)' : 'var(--success-border)'}`,
        borderRadius: 6,
        fontSize: 'var(--fs-xs)',
        color: err ? 'var(--danger)' : 'var(--success)',
      }}
    >
      {children}
    </div>
  );
}
